/**
 * Route guard for the Saee backoffice. Requires a dashboard role from GET /api/auth/me.
 * Security Checklist: client-side route authorization; must be paired with server-side checks.
 */

import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/AuthContext';
import { LOGIN_PATH, ACCESS_DENIED_PATH } from '@lib/routes';
import { getRequiredPermissionForPath } from '@lib/rbac';
import { usePermissions } from '@hooks/usePermissions';
import { AccessDeniedPage } from '@pages/AccessDeniedPage';

// 'viewer' is a legacy / test role treated as read-only support-agent access.
const DASHBOARD_ROLES = new Set(['system_administrator', 'operations_manager', 'financial_officer', 'support_agent', 'viewer']);

export function RouteGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { isAuthenticated, authLoading, userRole } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const role = String(userRole ?? '').trim().toLowerCase();

  const isPermissionDenied =
    !authLoading &&
    isAuthenticated &&
    DASHBOARD_ROLES.has(role) &&
    location.pathname !== ACCESS_DENIED_PATH &&
    (() => {
      const required = getRequiredPermissionForPath(location.pathname, location.search);
      return !!required && !can(required);
    })();

  useEffect(() => {
    if (isPermissionDenied) {
      navigate(ACCESS_DENIED_PATH, { replace: true });
    }
  }, [isPermissionDenied, navigate]);

  if (authLoading) {
    return (
      <span className="sr-only">
        {t('admin.auth.resolvingRole', { defaultValue: 'Loading session…' })}
      </span>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  if (!DASHBOARD_ROLES.has(role)) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  if (isPermissionDenied) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
}
